import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup, Spinner } from 'reactstrap';
import moment from 'moment';
import { collection, getDocs } from 'firebase/firestore';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';
import { useCollectionCache } from '../../services/collectionCache.service';
import { SELECTABLE_DURATIONS, calculateExpiryDate } from '../../constants/licenseDurations';
import { LicenseDurationType } from '../../types/license';

// Styles matching ShareLicenseModal pattern
const styles = () => createStyles({
  autocomplete: {
    width: '100%',
    '& .MuiInputBase-root': {
      backgroundColor: 'transparent',
      color: '#fff',
      minHeight: '32px',
      fontFamily: 'var(--font-family-primary)',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
        borderWidth: '1px'
      },
      '& .MuiAutocomplete-tag': {
        backgroundColor: 'var(--accent-1-color)',
        borderRadius: '8px',
        margin: '2px',
        padding: '4px 8px',
        color: '#fff',
        fontSize: 'var(--menu-font-size)',
        fontFamily: 'var(--font-family-primary)',
        '& .MuiChip-label': {
          padding: '0 4px'
        },
        '& .MuiChip-deleteIcon': {
          color: '#fff',
          fontSize: '16px',
          margin: '0 2px 0 -4px'
        }
      }
    },
    '& input': {
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)',
      fontFamily: 'var(--font-family-primary)',
      color: '#fff'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.1)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.2)'
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      color: '#ffffff',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
      }
    }
  },
  chip: {
    backgroundColor: 'var(--accent-1-color)',
    color: '#fff',
    margin: '2px',
    height: '24px',
    fontFamily: 'var(--font-family-primary)',
    '& .MuiChip-label': {
      padding: '0 6px',
      fontSize: '0.75rem',
      fontFamily: 'var(--font-family-primary)'
    },
    '& .MuiChip-deleteIcon': {
      color: '#fff',
      fontSize: '16px'
    }
  }
});

export interface LicenseRequestData {
  licenseType: string;
  dateExpires: number | null;
  dateStart: number | null;
  jobTitle?: string;
  client?: { label: string; value: string } | null;
  agency?: { label: string; value: string } | null;
  flightingPlatform?: { label: string; value: string }[];
  territories?: { label: string; value: string }[];
  notes?: string;
}

interface PrefillData {
  licenseType?: string;
  jobTitle?: string;
  client?: { label: string; value: string } | null;
  agency?: { label: string; value: string } | null;
  flightingPlatform?: { label: string; value: string }[];
  territories?: { label: string; value: string }[];
}

interface RequestLicenseModalProps extends WithStyles<typeof styles> {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (requestData: LicenseRequestData) => Promise<void>;
  trackName: string;
  sourceOrgName?: string;
  firebase: any;
  prefillData?: PrefillData | null;
}

interface SelectOption {
  label: string;
  value: string;
}

const RequestLicenseModal: React.FC<RequestLicenseModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  trackName,
  sourceOrgName,
  firebase,
  classes,
  prefillData,
}) => {
  // Duration state
  const [selectedDuration, setSelectedDuration] = useState<LicenseDurationType | null>(null);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [customDate, setCustomDate] = useState('');

  // License metadata state
  const [jobTitle, setJobTitle] = useState('');
  const [client, setClient] = useState<SelectOption | null>(null);
  const [agency, setAgency] = useState<SelectOption | null>(null);
  const [flightingPlatform, setFlightingPlatform] = useState<SelectOption[]>([]);
  const [territories, setTerritories] = useState<SelectOption[]>([]);
  const [notes, setNotes] = useState('');

  // Options from collections
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
  const [agencyOptions, setAgencyOptions] = useState<SelectOption[]>([]);
  const [flightingOptions, setFlightingOptions] = useState<SelectOption[]>([]);
  const [territoryOptions, setTerritoryOptions] = useState<SelectOption[]>([]);

  // Loading states
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get cached collections
  const collectionCache = useCollectionCache();

  // Load options from collections
  useEffect(() => {
    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        // Helper to load from cache or fallback to Firestore
        const loadGlobalCollection = async (
          cacheData: any[],
          collectionName: string,
          setOptions: (opts: SelectOption[]) => void
        ) => {
          if (cacheData.length > 0) {
            // Use item.value for consistency with AddAudioForm
            setOptions(cacheData.map(item => ({ label: item.displayName, value: item.value || item.id })));
          } else if (firebase?.db) {
            try {
              const snapshot = await getDocs(collection(firebase.db, collectionName));
              const opts = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  label: data.label || data.name || data.displayName || doc.id,
                  // Use data.value for consistency with AddAudioForm
                  value: data.value || doc.id,
                };
              });
              opts.sort((a, b) => a.label.localeCompare(b.label));
              setOptions(opts);
            } catch (err) {
              console.error(`Error loading ${collectionName}:`, err);
            }
          }
        };

        await Promise.all([
          loadGlobalCollection(collectionCache.client, 'client', setClientOptions),
          loadGlobalCollection(collectionCache.agency, 'agency', setAgencyOptions),
          loadGlobalCollection(collectionCache.territories, 'territories', setTerritoryOptions),
          loadGlobalCollection(collectionCache.flightingplatform, 'flightingplatform', setFlightingOptions),
        ]);
      } catch (error) {
        console.error('Error loading options:', error);
      }
      setIsLoadingOptions(false);
    };

    if (isOpen) {
      loadOptions();
    }
  }, [isOpen, firebase, collectionCache]);

  // Pre-fill from most recent license when modal opens
  useEffect(() => {
    if (isOpen && prefillData) {
      // Pre-fill license type if it's a valid selectable duration
      if (prefillData.licenseType &&
          SELECTABLE_DURATIONS.some(d => d.value === prefillData.licenseType)) {
        setSelectedDuration(prefillData.licenseType as LicenseDurationType);
      }

      // Pre-fill other fields
      if (prefillData.jobTitle) {
        setJobTitle(prefillData.jobTitle);
      }
      if (prefillData.client) {
        setClient(prefillData.client);
      }
      if (prefillData.agency) {
        setAgency(prefillData.agency);
      }
      if (prefillData.flightingPlatform && prefillData.flightingPlatform.length > 0) {
        setFlightingPlatform(prefillData.flightingPlatform);
      }
      if (prefillData.territories && prefillData.territories.length > 0) {
        setTerritories(prefillData.territories);
      }
    }
  }, [isOpen, prefillData]);

  // Reset state when modal closes
  const resetState = () => {
    setSelectedDuration(null);
    setShowCustomDate(false);
    setCustomDate('');
    setJobTitle('');
    setClient(null);
    setAgency(null);
    setFlightingPlatform([]);
    setTerritories([]);
    setNotes('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Duration selection
  const selectDuration = (durationType: LicenseDurationType | 'custom') => {
    if (durationType === 'custom') {
      setShowCustomDate(true);
      setSelectedDuration(null);
    } else {
      setShowCustomDate(false);
      setCustomDate('');
      setSelectedDuration(durationType);
    }
  };

  // Custom date handling
  const handleCustomDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^\d]/g, '');
    if (value.length > 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    if (value.length > 5) {
      value = value.slice(0, 5) + '/' + value.slice(5);
    }
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    setCustomDate(value);
    setSelectedDuration(null);
  };

  const isValidCustomDate = (dateStr: string): boolean => {
    if (!dateStr || dateStr.length !== 10) return false;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return false;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
    if (year < 2000 || year > 2099) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    const date = moment(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`, 'YYYY-MM-DD');
    return date.isValid() && date.isSameOrAfter(moment(), 'day');
  };

  // Submit request
  const handleSubmit = async () => {
    let dateExpires: number | null = null;
    let licenseType: string = 'none';

    if (showCustomDate && customDate && isValidCustomDate(customDate)) {
      const parts = customDate.split('/');
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      dateExpires = moment(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`, 'YYYY-MM-DD').endOf('day').valueOf();
      licenseType = 'custom';
    } else if (selectedDuration) {
      licenseType = selectedDuration;
      dateExpires = calculateExpiryDate(selectedDuration);
    }

    const requestData: LicenseRequestData = {
      licenseType,
      dateStart: Date.now(),
      dateExpires,
      jobTitle: jobTitle || undefined,
      client: client ? { label: client.label, value: client.value } : null,
      agency: agency ? { label: agency.label, value: agency.value } : null,
      flightingPlatform: flightingPlatform.map(fp => ({ label: fp.label, value: fp.value })),
      territories: territories.map(t => ({ label: t.label, value: t.value })),
      notes: notes || undefined,
    };

    setIsSubmitting(true);
    try {
      await onSubmit(requestData);
      resetState();
    } catch (error) {
      console.error('Error submitting license request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = !selectedDuration && !(showCustomDate && isValidCustomDate(customDate));

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="lg"
      style={{
        color: '#ffffff',
        maxWidth: '600px',
        margin: '1.75rem auto',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
        border: 'none',
        outline: 'none',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          fontSize: '1.25rem',
          fontWeight: '500',
        }}
        close={
          <button
            className="close"
            onClick={handleClose}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              padding: '0.25rem',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>Request License</span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          fontSize: '1rem',
          lineHeight: '1.5',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        <p style={{ color: '#fff', marginBottom: '0.5rem' }}>
          Request license for "<strong>{trackName}</strong>"
        </p>
        {sourceOrgName && (
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            From: {sourceOrgName}
          </p>
        )}

        {/* Duration Selection */}
        <div style={{ marginBottom: '1.5rem' }}>
          <Label style={{ color: '#fff', fontWeight: '500', marginBottom: '0.5rem' }}>
            Requested Duration <span style={{ color: 'var(--accent-1-color)' }}>*</span>
          </Label>
          <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
            {SELECTABLE_DURATIONS.map((option) => (
              <Button
                key={option.value}
                size="sm"
                className={selectedDuration === option.value ? 'btn-success-custom' : 'btn-primary-custom'}
                onClick={() => selectDuration(option.value)}
                style={{ position: 'relative', minWidth: '80px' }}
              >
                <span style={{ position: 'relative', zIndex: 3, color: '#ffffff' }}>{option.label}</span>
              </Button>
            ))}
            <Button
              size="sm"
              className={showCustomDate ? 'btn-success-custom' : 'btn-primary-custom'}
              onClick={() => selectDuration('custom')}
              style={{ position: 'relative', minWidth: '80px' }}
            >
              <span style={{ position: 'relative', zIndex: 3, color: '#ffffff' }}>Custom</span>
            </Button>
          </div>
          {showCustomDate && (
            <div className="mt-2">
              <Input
                type="text"
                value={customDate}
                onChange={handleCustomDateChange}
                placeholder="dd/mm/yyyy"
                maxLength={10}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${customDate && !isValidCustomDate(customDate) && customDate.length === 10 ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)'}`,
                  color: '#fff',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  maxWidth: '150px',
                }}
              />
              {customDate && customDate.length === 10 && !isValidCustomDate(customDate) && (
                <small style={{ color: 'var(--accent-1-color)', marginTop: '4px', display: 'block' }}>
                  Please enter a valid future date
                </small>
              )}
            </div>
          )}
        </div>

        {/* Job Title */}
        <FormGroup style={{ marginBottom: '1rem' }}>
          <Label style={{ color: '#fff', fontWeight: '500' }}>Job Title / Campaign</Label>
          <Input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g., Summer Campaign 2025"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#fff',
              borderRadius: '4px',
            }}
          />
        </FormGroup>

        {/* Client & Agency Row */}
        <div className="d-flex" style={{ gap: '1rem', marginBottom: '1rem' }}>
          <FormGroup style={{ flex: 1 }}>
            <Label style={{ color: '#fff', fontWeight: '500' }}>Brand</Label>
            <Autocomplete
              freeSolo
              options={clientOptions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
              value={client}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  setClient({ label: newValue, value: newValue });
                } else {
                  setClient(newValue);
                }
              }}
              loading={isLoadingOptions}
              className={classes.autocomplete}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select or type..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    label={typeof option === 'string' ? option : option.label}
                    {...getTagProps({ index })}
                    className={classes.chip}
                  />
                ))
              }
            />
          </FormGroup>
          <FormGroup style={{ flex: 1 }}>
            <Label style={{ color: '#fff', fontWeight: '500' }}>Agency</Label>
            <Autocomplete
              freeSolo
              options={agencyOptions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
              value={agency}
              onChange={(_, newValue) => {
                if (typeof newValue === 'string') {
                  setAgency({ label: newValue, value: newValue });
                } else {
                  setAgency(newValue);
                }
              }}
              loading={isLoadingOptions}
              className={classes.autocomplete}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select or type..."
                  size="small"
                />
              )}
            />
          </FormGroup>
        </div>

        {/* Flighting Platform */}
        <FormGroup style={{ marginBottom: '1rem' }}>
          <Label style={{ color: '#fff', fontWeight: '500' }}>Flighting Platform</Label>
          <Autocomplete
            multiple
            options={flightingOptions}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
            value={flightingPlatform}
            onChange={(_, newValue) => {
              const mapped = newValue.map(v =>
                typeof v === 'string' ? { label: v, value: v } : v
              );
              setFlightingPlatform(mapped);
            }}
            loading={isLoadingOptions}
            className={classes.autocomplete}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select platforms..."
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={typeof option === 'string' ? option : option.label}
                  {...getTagProps({ index })}
                  className={classes.chip}
                />
              ))
            }
          />
        </FormGroup>

        {/* Territories */}
        <FormGroup style={{ marginBottom: '1rem' }}>
          <Label style={{ color: '#fff', fontWeight: '500' }}>Territories</Label>
          <Autocomplete
            multiple
            options={territoryOptions}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
            value={territories}
            onChange={(_, newValue) => {
              const mapped = newValue.map(v =>
                typeof v === 'string' ? { label: v, value: v } : v
              );
              setTerritories(mapped);
            }}
            loading={isLoadingOptions}
            className={classes.autocomplete}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select territories..."
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={typeof option === 'string' ? option : option.label}
                  {...getTagProps({ index })}
                  className={classes.chip}
                />
              ))
            }
          />
        </FormGroup>

        {/* Notes */}
        <FormGroup style={{ marginBottom: '0' }}>
          <Label style={{ color: '#fff', fontWeight: '500' }}>Notes</Label>
          <Input
            type="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes or requirements..."
            rows={3}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#fff',
              borderRadius: '4px',
              resize: 'vertical',
            }}
          />
        </FormGroup>
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '1rem',
        }}
      >
        <Button
          className="btn-primary-custom"
          onClick={handleSubmit}
          disabled={isSubmitDisabled || isSubmitting}
          style={isSubmitDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="mr-2" /> Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </span>
        </Button>
        <Button className="btn-danger-custom" onClick={handleClose}>
          <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default withStyles(styles)(RequestLicenseModal);
